// WHAT: Settings view component for configuring normal ranges for Withings health metrics evaluation.
// WHY:  Provides users with the ability to customize normal ranges for body composition metrics (weight, body fat, muscle mass, etc.) based on their personal characteristics (gender, age, height), enabling personalized health evaluation.
// DOMAIN: Settings → Withings
// DEPENDENCIES: React, @/components/ui, @/domains/health/withings/services/normRangeService, lucide-react
// RISKS:
//   - NOTE: Normal ranges are calculated based on gender, age, and height; incorrect values may lead to inaccurate health evaluations.
//   - WARN: Custom ranges override default calculations; users should understand the implications before modifying.
//   - NOTE: Settings are stored locally; changes persist across sessions.

import { Activity, Edit3, CheckCircle, X, RotateCcw } from "lucide-react";
import { useState } from "react";

import { Card, Button, Input, Label, Select, Stack, Heading } from "@/components/ui";
import { NormRangeService } from "@/domains/health/withings/services/normRangeService";
import {
  type NormRangeSettings,
  type DEFAULT_NORM_RANGES,
} from "@/domains/health/withings/types/normRanges";

/**
 * WHAT:
 *   Settings view component for configuring normal ranges for Withings health metrics.
 *
 * WHY:
 *   Allows users to customize normal ranges for body composition metrics (weight, body fat, muscle mass, etc.)
 *   based on personal characteristics (gender, age, height), enabling personalized health evaluation.
 *
 * BEHAVIOR:
 *   - Loads current normal range settings from `NormRangeService` on mount.
 *   - Provides edit mode for modifying personal data (gender, age, height) and custom ranges.
 *   - Calculates and displays current normal ranges based on personal data and custom overrides.
 *   - Supports saving, canceling, and resetting to default values.
 *   - Displays normal ranges for multiple metrics (weight, body fat, muscle mass, fat mass, body water, bone mass, visceral fat, fat-free mass percent, BMI).
 *
 * RISKS:
 *   - NOTE: Normal ranges are calculated based on gender, age, and height; incorrect values may lead to inaccurate health evaluations.
 *   - WARN: Custom ranges override default calculations; users should understand the implications before modifying.
 *   - NOTE: Settings are stored locally via `NormRangeService`; changes persist across sessions.
 *
 * @returns {JSX.Element} Withings normal ranges configuration component
 *
 */
// Reason: Component is used internally by WithingsSettingsView via routing, not directly imported

export function WithingsNormRanges() {
  const [normSettings, setNormSettings] = useState<NormRangeSettings>(
    NormRangeService.loadSettings(),
  );
  const [isEditingNormRanges, setIsEditingNormRanges] = useState(false);
  const [temporaryNormSettings, setTemporaryNormSettings] =
    useState<NormRangeSettings>(normSettings);

  /**
   * WHAT:
   *   Saves the temporary normal range settings to persistent storage.
   *
   * WHY:
   *   To persist user-configured normal ranges and personal data for future health evaluations.
   *
   * BEHAVIOR:
   *   - Saves `tempNormSettings` via `NormRangeService.saveSettings`.
   *   - Updates the current `normSettings` state.
   *   - Exits edit mode.
   *
   * RISKS:
   *   - NOTE: Overwrites existing settings; no confirmation dialog is shown.
   */
  const handleSaveNormRanges = () => {
    NormRangeService.saveSettings(temporaryNormSettings);
    setNormSettings(temporaryNormSettings);
    setIsEditingNormRanges(false);
  };

  /**
   * WHAT:
   *   Cancels editing and reverts temporary settings to the last saved state.
   *
   * WHY:
   *   To allow users to discard unsaved changes without affecting the current configuration.
   *
   * BEHAVIOR:
   *   - Resets `tempNormSettings` to the current `normSettings`.
   *   - Exits edit mode.
   *
   * RISKS:
   *   - None apparent.
   */
  const handleCancelNormRanges = () => {
    setTemporaryNormSettings(normSettings);
    setIsEditingNormRanges(false);
  };

  /**
   * WHAT:
   *   Resets all normal range settings to default values.
   *
   * WHY:
   *   To allow users to restore factory defaults if they want to start over with their configuration.
   *
   * BEHAVIOR:
   *   - Calls `NormRangeService.resetToDefaults()` to get default settings.
   *   - Updates both `normSettings` and `tempNormSettings` with defaults.
   *   - Settings are persisted immediately.
   *
   * RISKS:
   *   - WARN: This action overwrites all custom ranges; no confirmation dialog is shown.
   */
  const handleResetNormRanges = () => {
    const defaultSettings = NormRangeService.resetToDefaults();
    setNormSettings(defaultSettings);
    setTemporaryNormSettings(defaultSettings);
  };

  /**
   * WHAT:
   *   Updates a specific field in the temporary normal range settings.
   *
   * WHY:
   *   To allow users to modify settings in edit mode without immediately persisting changes.
   *
   * INPUTS:
   *   - key: keyof NormRangeSettings — The setting key to update (e.g., 'gender', 'age', 'height').
   *   - value: any — The new value for the setting.
   *
   * BEHAVIOR:
   *   - Updates the `tempNormSettings` state with the new value for the specified key.
   *   - Uses functional state update to ensure immutability.
   *
   * RISKS:
   *   - NOTE: No validation is performed; invalid values may cause issues when saving.
   *
   * @param key
   *
   * @param value
   *
   */
  const updateTemporaryNormSetting = (key: keyof NormRangeSettings, value: unknown) => {
    setTemporaryNormSettings((previous) => ({ ...previous, [key]: value }));
  };

  /**
   * WHAT:
   *   Updates a custom range value (min or max) for a specific health metric.
   *
   * WHY:
   *   To allow users to override default normal ranges for individual metrics.
   *
   * INPUTS:
   *   - metric: keyof typeof DEFAULT_NORM_RANGES — The health metric to update (e.g., 'bodyFat', 'muscleMass').
   *   - key: 'min' | 'max' — Whether to update the minimum or maximum value.
   *   - value: number — The new range value.
   *
   * BEHAVIOR:
   *   - Updates the `tempNormSettings.customRanges[metric][key]` value.
   *   - Uses nested state updates to preserve other custom ranges.
   *
   * RISKS:
   *   - NOTE: No validation ensures min < max; invalid ranges may cause evaluation issues.
   *
   * @param metric
   *
   * @param key
   *
   * @param value
   *
   */
  const updateCustomRange = (
    metric: keyof typeof DEFAULT_NORM_RANGES,
    key: "max" | "min",
    value: number,
  ) => {
    setTemporaryNormSettings((previous) => ({
      ...previous,
      customRanges: {
        ...previous.customRanges,
        [metric]: {
          ...previous.customRanges[metric],
          [key]: value,
        },
      },
    }));
  };

  const currentRanges = NormRangeService.getCurrentNormRanges();

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-slate-800/30 border-slate-700/50" padding="lg" variant="default">
        <Stack align="center" direction="row" gap={4}>
          <div className="p-3 bg-tracq-bright-blue/10 rounded-lg">
            <Activity className="w-6 h-6 text-tracq-bright-blue" />
          </div>
          <Stack direction="col" gap={1}>
            <Heading className="text-xl font-semibold text-white" level={2}>
              Normal Range Settings
            </Heading>
            <p className="text-slate-300">
              Configure normal ranges for evaluating your health measurements and body composition
              data.
            </p>
          </Stack>
        </Stack>
      </Card>

      {/* Normbereich-Einstellungen */}
      <Card className="bg-slate-800/50 border-slate-700/50" padding="lg" variant="default">
        <Stack direction="col" gap={6}>
          {/* Header */}
          <Stack align="center" direction="row" justify="between">
            <Stack align="center" direction="row" gap={3}>
              <div className="p-2 bg-tracq-bright-blue/10 rounded-lg">
                <Activity className="w-5 h-5 text-tracq-bright-blue" />
              </div>
              <Heading className="text-lg font-medium text-white" level={3}>
                Health Metrics Normal Ranges
              </Heading>
            </Stack>

            <Stack direction="row" gap={2}>
              {isEditingNormRanges ? (
                <>
                  <Button onClick={handleSaveNormRanges} size="md" variant="primary">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Save
                  </Button>
                  <Button onClick={handleCancelNormRanges} size="md" variant="ghost">
                    <X className="w-4 h-4 mr-2" />
                    Cancel
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() => {
                    setIsEditingNormRanges(true);
                  }}
                  size="md"
                  variant="secondary"
                >
                  <Edit3 className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              )}
              <Button onClick={handleResetNormRanges} size="md" variant="danger">
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset
              </Button>
            </Stack>
          </Stack>

          <p className="text-slate-400 text-sm">
            These values are used for evaluating your measurements. You can customize them
            individually.
          </p>

          {/* Personal Data */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Stack direction="col" gap={2}>
              <Label className="text-slate-300">Gender</Label>
              <Select
                disabled={!isEditingNormRanges}
                onChange={(value) => {
                  updateTemporaryNormSetting("gender", value);
                }}
                options={[
                  { label: "Male", value: "male" },
                  { label: "Female", value: "female" },
                ]}
                size="md"
                value={temporaryNormSettings.gender}
              />
            </Stack>

            <Stack direction="col" gap={2}>
              <Label className="text-slate-300">Age</Label>
              <Input
                disabled={!isEditingNormRanges}
                max="100"
                min="16"
                onChange={(e) => {
                  updateTemporaryNormSetting("age", Number.parseInt(e.target.value));
                }}
                size="md"
                type="number"
                value={temporaryNormSettings.age}
              />
            </Stack>

            <Stack direction="col" gap={2}>
              <Label className="text-slate-300">Height (cm)</Label>
              <Input
                disabled={!isEditingNormRanges}
                max="250"
                min="100"
                onChange={(e) => {
                  updateTemporaryNormSetting("height", Number.parseInt(e.target.value));
                }}
                size="md"
                type="number"
                value={temporaryNormSettings.height}
              />
            </Stack>
          </div>

          {/* Normal Ranges Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Weight */}
            <Card className="bg-slate-700/50" padding="md" variant="default">
              <Stack direction="col" gap={3}>
                <Heading className="text-md font-medium text-white" level={4}>
                  Weight
                </Heading>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-300">Min:</span>
                    <span className="text-sm text-white font-medium">
                      {currentRanges.weight.min.toFixed(1)} kg
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-300">Max:</span>
                    <span className="text-sm text-white font-medium">
                      {currentRanges.weight.max.toFixed(1)} kg
                    </span>
                  </div>
                  <div className="text-xs text-slate-400 mt-2">
                    {currentRanges.weight.description}
                  </div>
                </div>
              </Stack>
            </Card>

            {/* Body Fat */}
            <Card className="bg-slate-700/50" padding="md" variant="default">
              <Stack direction="col" gap={3}>
                <Heading className="text-md font-medium text-white" level={4}>
                  Body Fat
                </Heading>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-300">Min:</span>
                    <Input
                      className="w-20"
                      disabled={!isEditingNormRanges}
                      max="100"
                      min="0"
                      onChange={(e) => {
                        updateCustomRange("bodyFat", "min", Number.parseFloat(e.target.value));
                      }}
                      size="sm"
                      step="0.1"
                      type="number"
                      value={
                        temporaryNormSettings.customRanges.bodyFat?.min ?? currentRanges.bodyFat.min
                      }
                    />
                    <span className="text-sm text-white">%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-300">Max:</span>
                    <Input
                      className="w-20"
                      disabled={!isEditingNormRanges}
                      max="100"
                      min="0"
                      onChange={(e) => {
                        updateCustomRange("bodyFat", "max", Number.parseFloat(e.target.value));
                      }}
                      size="sm"
                      step="0.1"
                      type="number"
                      value={
                        temporaryNormSettings.customRanges.bodyFat?.max ?? currentRanges.bodyFat.max
                      }
                    />
                    <span className="text-sm text-white">%</span>
                  </div>
                  <div className="text-xs text-slate-400 mt-2">
                    {currentRanges.bodyFat.description}
                  </div>
                </div>
              </Stack>
            </Card>

            {/* Muscle Mass */}
            <Card className="bg-slate-700/50" padding="md" variant="default">
              <Stack direction="col" gap={3}>
                <Heading className="text-md font-medium text-white" level={4}>
                  Muscle Mass
                </Heading>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-300">Min:</span>
                    <Input
                      className="w-20"
                      disabled={!isEditingNormRanges}
                      max="100"
                      min="0"
                      onChange={(e) => {
                        updateCustomRange("muscleMass", "min", Number.parseFloat(e.target.value));
                      }}
                      size="sm"
                      step="0.1"
                      type="number"
                      value={
                        temporaryNormSettings.customRanges.muscleMass?.min ??
                        currentRanges.muscleMass.min
                      }
                    />
                    <span className="text-sm text-white">%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-300">Max:</span>
                    <Input
                      className="w-20"
                      disabled={!isEditingNormRanges}
                      max="100"
                      min="0"
                      onChange={(e) => {
                        updateCustomRange("muscleMass", "max", Number.parseFloat(e.target.value));
                      }}
                      size="sm"
                      step="0.1"
                      type="number"
                      value={
                        temporaryNormSettings.customRanges.muscleMass?.max ??
                        currentRanges.muscleMass.max
                      }
                    />
                    <span className="text-sm text-white">%</span>
                  </div>
                  <div className="text-xs text-slate-400 mt-2">
                    {currentRanges.muscleMass.description}
                  </div>
                </div>
              </Stack>
            </Card>

            {/* Fat Mass */}
            <Card className="bg-slate-700/50" padding="md" variant="default">
              <Stack direction="col" gap={3}>
                <Heading className="text-md font-medium text-white" level={4}>
                  Fat Mass
                </Heading>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-300">Min:</span>
                    <Input
                      className="w-20"
                      disabled={!isEditingNormRanges}
                      max="100"
                      min="0"
                      onChange={(e) => {
                        updateCustomRange("fatMass", "min", Number.parseFloat(e.target.value));
                      }}
                      size="sm"
                      step="0.1"
                      type="number"
                      value={
                        temporaryNormSettings.customRanges.fatMass?.min ?? currentRanges.fatMass.min
                      }
                    />
                    <span className="text-sm text-white">kg</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-300">Max:</span>
                    <Input
                      className="w-20"
                      disabled={!isEditingNormRanges}
                      max="100"
                      min="0"
                      onChange={(e) => {
                        updateCustomRange("fatMass", "max", Number.parseFloat(e.target.value));
                      }}
                      size="sm"
                      step="0.1"
                      type="number"
                      value={
                        temporaryNormSettings.customRanges.fatMass?.max ?? currentRanges.fatMass.max
                      }
                    />
                    <span className="text-sm text-white">kg</span>
                  </div>
                  <div className="text-xs text-slate-400 mt-2">
                    {currentRanges.fatMass.description}
                  </div>
                </div>
              </Stack>
            </Card>

            {/* Body Water */}
            <Card className="bg-slate-700/50" padding="md" variant="default">
              <Stack direction="col" gap={3}>
                <Heading className="text-md font-medium text-white" level={4}>
                  Body Water
                </Heading>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-300">Min:</span>
                    <Input
                      className="w-20"
                      disabled={!isEditingNormRanges}
                      max="100"
                      min="0"
                      onChange={(e) => {
                        updateCustomRange("bodyWater", "min", Number.parseFloat(e.target.value));
                      }}
                      size="sm"
                      step="0.1"
                      type="number"
                      value={
                        temporaryNormSettings.customRanges.bodyWater?.min ??
                        currentRanges.bodyWater.min
                      }
                    />
                    <span className="text-sm text-white">%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-300">Max:</span>
                    <Input
                      className="w-20"
                      disabled={!isEditingNormRanges}
                      max="100"
                      min="0"
                      onChange={(e) => {
                        updateCustomRange("bodyWater", "max", Number.parseFloat(e.target.value));
                      }}
                      size="sm"
                      step="0.1"
                      type="number"
                      value={
                        temporaryNormSettings.customRanges.bodyWater?.max ??
                        currentRanges.bodyWater.max
                      }
                    />
                    <span className="text-sm text-white">%</span>
                  </div>
                  <div className="text-xs text-slate-400 mt-2">
                    {currentRanges.bodyWater.description}
                  </div>
                </div>
              </Stack>
            </Card>

            {/* Bone Mass */}
            <Card className="bg-slate-700/50" padding="md" variant="default">
              <Stack direction="col" gap={3}>
                <Heading className="text-md font-medium text-white" level={4}>
                  Bone Mass
                </Heading>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-300">Min:</span>
                    <Input
                      className="w-20"
                      disabled={!isEditingNormRanges}
                      max="10"
                      min="0"
                      onChange={(e) => {
                        updateCustomRange("boneMass", "min", Number.parseFloat(e.target.value));
                      }}
                      size="sm"
                      step="0.1"
                      type="number"
                      value={
                        temporaryNormSettings.customRanges.boneMass?.min ??
                        currentRanges.boneMass.min
                      }
                    />
                    <span className="text-sm text-white">%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-300">Max:</span>
                    <Input
                      className="w-20"
                      disabled={!isEditingNormRanges}
                      max="10"
                      min="0"
                      onChange={(e) => {
                        updateCustomRange("boneMass", "max", Number.parseFloat(e.target.value));
                      }}
                      size="sm"
                      step="0.1"
                      type="number"
                      value={
                        temporaryNormSettings.customRanges.boneMass?.max ??
                        currentRanges.boneMass.max
                      }
                    />
                    <span className="text-sm text-white">%</span>
                  </div>
                  <div className="text-xs text-slate-400 mt-2">
                    {currentRanges.boneMass.description}
                  </div>
                </div>
              </Stack>
            </Card>

            {/* Visceral Fat */}
            <Card className="bg-slate-700/50" padding="md" variant="default">
              <Stack direction="col" gap={3}>
                <Heading className="text-md font-medium text-white" level={4}>
                  Visceral Fat
                </Heading>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-300">Min:</span>
                    <Input
                      className="w-20"
                      disabled={!isEditingNormRanges}
                      max="50"
                      min="0"
                      onChange={(e) => {
                        updateCustomRange("visceralFat", "min", Number.parseFloat(e.target.value));
                      }}
                      size="sm"
                      step="0.1"
                      type="number"
                      value={
                        temporaryNormSettings.customRanges.visceralFat?.min ??
                        currentRanges.visceralFat.min
                      }
                    />
                    <span className="text-sm text-white">Index</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-300">Max:</span>
                    <Input
                      className="w-20"
                      disabled={!isEditingNormRanges}
                      max="50"
                      min="0"
                      onChange={(e) => {
                        updateCustomRange("visceralFat", "max", Number.parseFloat(e.target.value));
                      }}
                      size="sm"
                      step="0.1"
                      type="number"
                      value={
                        temporaryNormSettings.customRanges.visceralFat?.max ??
                        currentRanges.visceralFat.max
                      }
                    />
                    <span className="text-sm text-white">Index</span>
                  </div>
                  <div className="text-xs text-slate-400 mt-2">
                    {currentRanges.visceralFat.description}
                  </div>
                </div>
              </Stack>
            </Card>

            {/* Fat Free Mass Percent */}
            <Card className="bg-slate-700/50" padding="md" variant="default">
              <Stack direction="col" gap={3}>
                <Heading className="text-md font-medium text-white" level={4}>
                  Fat Free Mass (Percent)
                </Heading>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-300">Min:</span>
                    <Input
                      className="w-20"
                      disabled={!isEditingNormRanges}
                      max="100"
                      min="0"
                      onChange={(e) => {
                        updateCustomRange(
                          "fatFreeMassPercent",
                          "min",
                          Number.parseFloat(e.target.value),
                        );
                      }}
                      size="sm"
                      step="0.1"
                      type="number"
                      value={
                        temporaryNormSettings.customRanges.fatFreeMassPercent?.min ??
                        currentRanges.fatFreeMassPercent.min
                      }
                    />
                    <span className="text-sm text-white">%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-300">Max:</span>
                    <Input
                      className="w-20"
                      disabled={!isEditingNormRanges}
                      max="100"
                      min="0"
                      onChange={(e) => {
                        updateCustomRange(
                          "fatFreeMassPercent",
                          "max",
                          Number.parseFloat(e.target.value),
                        );
                      }}
                      size="sm"
                      step="0.1"
                      type="number"
                      value={
                        temporaryNormSettings.customRanges.fatFreeMassPercent?.max ??
                        currentRanges.fatFreeMassPercent.max
                      }
                    />
                    <span className="text-sm text-white">%</span>
                  </div>
                  <div className="text-xs text-slate-400 mt-2">
                    {currentRanges.fatFreeMassPercent.description}
                  </div>
                </div>
              </Stack>
            </Card>

            {/* BMI */}
            <Card className="bg-slate-700/50" padding="md" variant="default">
              <Stack direction="col" gap={3}>
                <Heading className="text-md font-medium text-white" level={4}>
                  BMI
                </Heading>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-300">Min:</span>
                    <Input
                      className="w-20"
                      disabled={!isEditingNormRanges}
                      max="40"
                      min="10"
                      onChange={(e) => {
                        updateCustomRange("bmi", "min", Number.parseFloat(e.target.value));
                      }}
                      size="sm"
                      step="0.1"
                      type="number"
                      value={temporaryNormSettings.customRanges.bmi?.min ?? currentRanges.bmi.min}
                    />
                    <span className="text-sm text-white">BMI</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-300">Max:</span>
                    <Input
                      className="w-20"
                      disabled={!isEditingNormRanges}
                      max="40"
                      min="10"
                      onChange={(e) => {
                        updateCustomRange("bmi", "max", Number.parseFloat(e.target.value));
                      }}
                      size="sm"
                      step="0.1"
                      type="number"
                      value={temporaryNormSettings.customRanges.bmi?.max ?? currentRanges.bmi.max}
                    />
                    <span className="text-sm text-white">BMI</span>
                  </div>
                  <div className="text-xs text-slate-400 mt-2">{currentRanges.bmi.description}</div>
                </div>
              </Stack>
            </Card>
          </div>

          {/* Information */}
          <Card className="bg-slate-700/30" padding="md" variant="default">
            <Stack direction="col" gap={2}>
              <Heading className="text-md font-medium text-white" level={4}>
                Information
              </Heading>
              <div className="text-sm text-slate-400 space-y-1">
                <p>
                  • <strong>Normal ranges</strong> are used for evaluating your measurements
                </p>
                <p>
                  • <strong>Weight</strong> is automatically calculated based on your height and
                  gender
                </p>
                <p>
                  • <strong>Individual adjustments</strong> override the default values
                </p>
                <p>
                  • <strong>Fat Free Mass (Percent)</strong> is automatically calculated based on
                  your body fat settings
                </p>
                <p>
                  • <strong>BMI</strong> is calculated for your fixed height of 183cm
                </p>
                <p>• All settings are stored locally in your browser</p>
              </div>
            </Stack>
          </Card>
        </Stack>
      </Card>
    </div>
  );
}
